import _ from "lodash";
import clsx from "clsx";
import React, { useContext } from 'react';
import * as PropTypes from 'prop-types';
import { buyNowContext } from "../BuyNow";
import TotalPrice from "./TotalPrice";
import {formattedNextChargeDate} from "../../utils/orderHelper";
import {PORTALS, PRODUCT_CATEGORIES} from "../../../../constants";

const OrderDetails = ({ showOrderDetails }) => {
  const { lineItems, shippingStageState, paymentStageState, order, promoCode, portal, quantitiesEnabled } = useContext(buyNowContext);

  const scaleLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const selectedScale = scaleLineItem ? scaleLineItem.product : null;
  const selectedPlan = planLineItem ? planLineItem.product : null;

  const getLastFourCharOfString = (string) => {
    return string.slice(string.length - 4)
  };

  const scaleExists = !_.isEmpty(selectedScale);

  let licenseKeyMessage = "";

  if (planLineItem) {
    if (planLineItem.quantity > 1)
      licenseKeyMessage = `Each license key will be linked to a plan you just purchased. Each license key supports a maximum of ${selectedPlan.allowableUsers} account(s).`;
    else
      licenseKeyMessage = `This license key is linked to the plan you just purchased. The license key supports a maximum of ${selectedPlan.allowableUsers} account(s).`;
  }

  let paymentMethodMessage = "";
  if (paymentStageState.creditCardNumber)
    paymentMethodMessage = `Visa ending in ${getLastFourCharOfString(paymentStageState.creditCardNumber)}`;
  else
    paymentMethodMessage = "Payment via PayPal";

  /**
   * Produce UI to display the name of a line item.
   * @param {LineItem} lineItem
   */
  const lineItemName = lineItem => {
    return <p key={lineItem.product.id} className="sub-txt text-muted text-capitalize p-0">{lineItem.product.lineItemName}</p>;
  };

  /**
   * Produce UI to display the description of a line item.
   * @param {LineItem} lineItem
   */
  const lineItemDescription = lineItem => {
    let desc = lineItem.product.lineItemDescription;

    // If quantities are enabled, we need to display the quantity of the product rather than a description.
    if (quantitiesEnabled)
      desc = `Qty: ${lineItem.quantity}`;

    return <p key={lineItem.product.id} className="sub-txt text-muted text-capitalize p-0">{desc}</p>;
  };

  return (
    <React.Fragment>
      <div id={"summary"} className={clsx("w-100 vh-100 bg-dark d-lg-none order-summary-mobile order-summary-mobile-background d-lg-block", showOrderDetails ? "d-block" : "d-none")}/>
      <div className={clsx("col-lg-4 order-summary-mobile d-lg-block", showOrderDetails ? "d-block" : "d-none")}>
        <div className="p-0 border rounded-sm shadow bg-white border-light order-summary-mobile order-summary-container" style={{ width: "100%" }}>
          <div className="p-3">
            <p className="header-txt text-dark fw-bold mb-0 pt-3 pt-lg-0">Order details</p>
            <div className="row justify-content-between">
              <div className="col-8 d-flex flex-column">
                <p className="plan-main-txt text-dark mb-0 pb-0 fw-bolder">Confirmation number</p>

                {lineItems.map(lineItem => lineItemName(lineItem))}
              </div>
              <div className="col-4 d-flex flex-column text-end">
                <p className="plan-main-txt fw-bolder text-dark p-0">{order.confirmationToken}</p>

                {lineItems.map(lineItem => lineItemDescription(lineItem))}
              </div>
            </div>

            {selectedPlan &&
              <div className="row justify-content-between mt-2">
                <div className="col-8 d-flex flex-column">
                  <p className="plan-main-txt text-dark mb-0 pb-0 fw-bolder">{`Unique license ${planLineItem.quantity > 1 ? 'keys' : 'key'}`}</p>
                  <p className="sub-txt text-muted pb-0">{licenseKeyMessage}</p>
                </div>
                <div className="col-4 text-end">
                  {
                    order.licenseKeys.map(licenseKey => (
                      <p key={licenseKey.key} className="plan-main-txt fw-bolder text-dark p-0" style={{ padding: 0 }}>{licenseKey.key}</p>
                    ))
                  }
                </div>
              </div>
            }

            {scaleExists &&
              <div className="row justify-content-between mt-2">
                <div className="col-8 d-flex flex-column">
                  <p className="plan-main-txt text-dark mb-0 pb-0 fw-bolder">Shipping</p>
                  <p className="sub-txt text-muted pb-0">
                    {shippingStageState.firstName} {shippingStageState.lastName}
                    <br/>
                    {shippingStageState.address1}
                    <br/>
                    {shippingStageState.city}, {shippingStageState.state} {shippingStageState.zipCode}
                  </p>
                </div>
                {/* Don't show an estimated shipping timeframe if shipping options aren't available for the selected scale. We might not have a good estimate in this case. */}
                {selectedScale && selectedScale.shippingOptionsAvailable &&
                  <div className="col-4 text-end">
                    <p className="plan-main-txt fw-bolder text-dark">{shippingStageState.shippingMethod.timeFrame}</p>
                  </div>
                }
              </div>
            }

            <div className="row justify-content-between mt-1">
              <div className="col-8 d-flex flex-column">
                <p className="plan-main-txt text-dark mb-0 pb-0 fw-bolder">Payment total</p>
                <p className="sub-txt text-muted pb-0">{paymentMethodMessage}</p>
                <p className="sub-txt text-muted">{paymentStageState.nameOnCard}</p>
              </div>
              <div className="col-4 text-end">
                <p className="plan-main-txt fw-bolder text-dark">${<TotalPrice />}</p>
              </div>
            </div>
          </div>

          {selectedPlan && selectedPlan.recurringPlan &&
            <div className="position-absolute mt-3 d-none d-lg-block">
              <p className="sub-txt">
                Your subscription will automatically renew unless you cancel before {formattedNextChargeDate(selectedPlan, promoCode)}. Your cancellation will take effect at the end of your current billing cycle.
              </p>
            </div>
          }
        </div>
      </div>
    </React.Fragment>
  )
};

OrderDetails.protoTypes = {
  showOrderDetails: PropTypes.bool.isRequired,
};

export default OrderDetails;